import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { useAuth } from "../../context/AuthContext";

export default function AdminSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [newAdmin, setNewAdmin] = useState({ name: "", email: "" });
  const [adminCreds, setAdminCreds] = useState(null);
  const { user } = useAuth();

  const loadAdmins = () => api.get("/admin/staff").then(({ data }) => setAdmins(data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) =>
      setS({
        site_url: data.site_url || "",
        smtp: data.smtp || {},
        twilio: data.twilio || {},
        stripe: data.stripe || {},
      })
    ).catch((e) => toast.error(apiError(e)));
    loadAdmins();
  }, []);

  const addAdmin = async () => {
    try {
      const { data } = await api.post("/admin/staff", newAdmin);
      setAdminCreds({ email: newAdmin.email, password: data.temp_password, email_status: data.email_status });
      setNewAdmin({ name: "", email: "" });
      loadAdmins();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeAdmin = async (uid) => {
    if (!window.confirm("Remove this admin's access?")) return;
    try {
      await api.delete(`/admin/staff/${uid}`);
      loadAdmins();
      toast.success("Admin removed");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!s) return <p className="text-neutral-400">Loading…</p>;

  const setNested = (section, k, v) => setS({ ...s, [section]: { ...s[section], [k]: v } });

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl" data-testid="admin-settings-page">
      <h1 className="text-3xl font-black">Platform settings</h1>
      <p className="text-sm text-neutral-500 mt-1">
        Site-wide fallbacks — businesses can override SMTP and Twilio in their own settings.
      </p>

      <Tabs defaultValue="general" className="mt-8">
        <TabsList className="bg-white border border-[#E5E5E5]" data-testid="admin-settings-tabs">
          <TabsTrigger value="general" data-testid="tab-general">General</TabsTrigger>
          <TabsTrigger value="admins" data-testid="tab-admins">Admin users</TabsTrigger>
          <TabsTrigger value="smtp" data-testid="tab-admin-smtp">SMTP fallback</TabsTrigger>
          <TabsTrigger value="twilio" data-testid="tab-admin-twilio">Twilio fallback</TabsTrigger>
          <TabsTrigger value="stripe" data-testid="tab-admin-stripe">Stripe</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6">
          <div className="space-y-2">
            <Label>Site URL (used in email links)</Label>
            <Input value={s.site_url} onChange={(e) => setS({ ...s, site_url: e.target.value })} data-testid="site-url-input" />
          </div>
        </TabsContent>

        <TabsContent value="admins" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6" data-testid="admins-tab">
          <p className="text-sm text-neutral-500">Additional super admins get full platform control with their own login.</p>
          <div className="space-y-3">
            {admins.map((a) => (
              <div key={a.id} className="border border-[#E5E5E5] rounded-xl p-4 flex items-center justify-between gap-4" data-testid={`admin-user-${a.id}`}>
                <div className="min-w-0">
                  <div className="font-medium text-sm">{a.name}</div>
                  <div className="text-xs text-neutral-400">{a.email}</div>
                </div>
                {a.id !== user?.id && (
                  <Button variant="ghost" size="sm" className="text-red-600 shrink-0" onClick={() => removeAdmin(a.id)} data-testid={`admin-remove-${a.id}`}>Remove</Button>
                )}
              </div>
            ))}
          </div>
          <div className="pt-4 border-t border-[#E5E5E5] space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input placeholder="Name" value={newAdmin.name} onChange={(e) => setNewAdmin({ ...newAdmin, name: e.target.value })} data-testid="admin-name-input" />
              <Input type="email" placeholder="Email" value={newAdmin.email} onChange={(e) => setNewAdmin({ ...newAdmin, email: e.target.value })} data-testid="admin-email-input" />
            </div>
            <Button variant="outline" className="rounded-full" onClick={addAdmin} disabled={!newAdmin.name || !newAdmin.email} data-testid="admin-add-button">Add admin</Button>
          </div>
        </TabsContent>

        <TabsContent value="smtp" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>SMTP host</Label>
              <Input value={s.smtp.host || ""} onChange={(e) => setNested("smtp", "host", e.target.value)} data-testid="admin-smtp-host-input" />
            </div>
            <div className="space-y-2">
              <Label>Port</Label>
              <Input type="number" value={s.smtp.port || 587} onChange={(e) => setNested("smtp", "port", Number(e.target.value))} data-testid="admin-smtp-port-input" />
              <p className="text-xs text-neutral-400">465 = SSL (automatic), 587/25 = STARTTLS</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Username</Label>
              <Input value={s.smtp.username || ""} onChange={(e) => setNested("smtp", "username", e.target.value)} data-testid="admin-smtp-username-input" />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={s.smtp.password || ""} onChange={(e) => setNested("smtp", "password", e.target.value)} data-testid="admin-smtp-password-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>From email</Label>
              <Input value={s.smtp.from_email || ""} onChange={(e) => setNested("smtp", "from_email", e.target.value)} data-testid="admin-smtp-from-email-input" />
            </div>
            <div className="space-y-2">
              <Label>From name</Label>
              <Input value={s.smtp.from_name || ""} onChange={(e) => setNested("smtp", "from_name", e.target.value)} data-testid="admin-smtp-from-name-input" />
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Switch checked={s.smtp.use_tls !== false} onCheckedChange={(v) => setNested("smtp", "use_tls", v)} data-testid="admin-smtp-tls-switch" />
            <Label>Use TLS/STARTTLS</Label>
          </div>
        </TabsContent>

        <TabsContent value="twilio" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6">
          <div className="space-y-2">
            <Label>Account SID</Label>
            <Input value={s.twilio.account_sid || ""} onChange={(e) => setNested("twilio", "account_sid", e.target.value)} data-testid="admin-twilio-sid-input" />
          </div>
          <div className="space-y-2">
            <Label>Auth token</Label>
            <Input type="password" value={s.twilio.auth_token || ""} onChange={(e) => setNested("twilio", "auth_token", e.target.value)} data-testid="admin-twilio-token-input" />
          </div>
          <div className="space-y-2">
            <Label>From number (E.164)</Label>
            <Input value={s.twilio.from_number || ""} onChange={(e) => setNested("twilio", "from_number", e.target.value)} data-testid="admin-twilio-from-input" />
          </div>
        </TabsContent>

        <TabsContent value="stripe" className="mt-6 space-y-5 bg-white border border-[#E5E5E5] rounded-2xl p-6">
          <div className="space-y-2">
            <Label>Secret key</Label>
            <Input type="password" value={s.stripe.secret_key || ""} onChange={(e) => setNested("stripe", "secret_key", e.target.value)} placeholder="sk_test_… / sk_live_…" data-testid="admin-stripe-secret-input" />
          </div>
          <div className="space-y-2">
            <Label>Publishable key</Label>
            <Input value={s.stripe.publishable_key || ""} onChange={(e) => setNested("stripe", "publishable_key", e.target.value)} placeholder="pk_test_…" data-testid="admin-stripe-publishable-input" />
          </div>
          <div className="space-y-2">
            <Label>Webhook secret</Label>
            <Input type="password" value={s.stripe.webhook_secret || ""} onChange={(e) => setNested("stripe", "webhook_secret", e.target.value)} placeholder="whsec_…" data-testid="admin-stripe-webhook-input" />
          </div>
          <p className="text-xs text-neutral-400">
            Webhook endpoint: <code>/api/stripe/webhook</code> — listen for checkout.session.completed.
          </p>
        </TabsContent>
      </Tabs>

      <Dialog open={!!adminCreds} onOpenChange={(open) => !open && setAdminCreds(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="admin-credentials-dialog">
          <DialogHeader>
            <DialogTitle>Admin login created</DialogTitle>
          </DialogHeader>
          {adminCreds && (
            <div className="space-y-4">
              <div className="bg-neutral-50 border border-[#E5E5E5] rounded-xl p-4 text-sm space-y-1.5">
                <div>Email: <b data-testid="admin-creds-email">{adminCreds.email}</b></div>
                <div>Temporary password: <b data-testid="admin-creds-password">{adminCreds.password}</b></div>
              </div>
              <p className={`text-xs ${adminCreds.email_status === "sent" ? "text-emerald-700" : "text-amber-700"}`}>
                {adminCreds.email_status === "sent"
                  ? "The welcome email was sent."
                  : "SMTP is not configured, so no email was delivered — share these credentials manually."}
              </p>
              <Button
                variant="outline"
                className="w-full rounded-full"
                onClick={() => {
                  navigator.clipboard.writeText(`Email: ${adminCreds.email}\nPassword: ${adminCreds.password}`);
                  toast.success("Copied to clipboard");
                }}
                data-testid="admin-creds-copy-button"
              >
                Copy credentials
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Button onClick={save} disabled={saving} className="mt-6 rounded-full px-8" data-testid="admin-settings-save-button">
        {saving ? "Saving…" : "Save settings"}
      </Button>
    </div>
  );
}
